import React from 'react';
import { POINTS_AWARD } from '@api/utils/gamification';
import { Link } from 'react-router-dom';
import { Button } from 'src/components/common/Button';
import { LOGIN_ROUTE } from 'src/constants/routes';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { user as userApi } from 'src/store/api/user';
import { updatePoints } from 'src/store/user';
import { selectAuth } from 'src/store/user/selectors';
import prayerStats from 'src/utils/stats';

import { GoogleIcon } from './google-icon';
import { PointsIcon } from './points-icon';
import css from './points-screen.module.scss';
import { WaIcon } from './wa-icon';

interface Props {
  className?: string;
  prayerHubId?: string;
  ref: React.Ref<HTMLDivElement | null>;
}
export const PointsScreen: React.FC<Props> = ({ className = '', prayerHubId, ref }) => {
  const auth = useAppSelector(selectAuth);
  const dispatch = useAppDispatch();
  const targetPoints = 25;
  const localRef = React.useRef<HTMLDivElement | null>(null);
  const [animateKey, setAnimateKey] = React.useState(0);
  const [durationLabel, setDurationLabel] = React.useState('4:27');
  const [durationUnit, setDurationUnit] = React.useState<'seconds' | 'minutes'>('minutes');
  const [updateStats, updateStatsState] = userApi.endpoints.updateStats.useMutation();

  React.useEffect(() => {
    const el = localRef.current;
    if (!el) return;

    const onShow = () => setAnimateKey(k => k + 1);
    el.addEventListener('pointsScreen:show', onShow as EventListener);
    return () => el.removeEventListener('pointsScreen:show', onShow as EventListener);
  }, []);

  React.useEffect(() => {
    if (!animateKey || !prayerHubId) return;

    try {
      prayerStats.endPrayer(prayerHubId);

      const prayerDuration = prayerStats.getDurationByPrayerId(prayerHubId);
      const totalSeconds = Math.round(prayerDuration / 1000);
      const formatted = formatPrayerDuration(totalSeconds);

      if (auth.isLoggedIn) {
        dispatch(updatePoints(POINTS_AWARD.PRAYER_COMPLETED));
        updateStats({ prayerHubId, prayerDuration });
      }

      setDurationLabel(formatted.label);
      setDurationUnit(formatted.unit);
    } catch {
      // ignore (storage can fail in some environments)
    }
  }, [animateKey]);

  const [isCountActive, setIsCountActive] = React.useState(false);
  React.useEffect(() => {
    if (!animateKey) return;
    setIsCountActive(false);
    const t = setTimeout(() => setIsCountActive(true), 520);
    return () => clearTimeout(t);
  }, [animateKey]);

  const animatedPoints = useCountUp({ to: targetPoints, durationMs: 520, isActive: isCountActive, key: animateKey });

  return (
    // @ts-ignore
    <div
      ref={(node: HTMLDivElement | null) => {
        localRef.current = node;
        // @ts-ignore
        if (typeof ref === 'function') ref(node);
        // @ts-ignore
        else if (ref) ref.current = node;
      }}
      className={`${css.pointsScreen} ${animateKey ? css.animate : ''} ${className}`.trim()}
      data-animate-key={animateKey}
    >
      <div className={css.title}>
        {durationUnit === 'seconds'
          ? `תודה שהקדשת ${durationLabel} שניות בשביל האחר!`
          : `תודה שהקדשת ${durationLabel} דקות בשביל האחר!`}
      </div>
      <div className={css.content}>
        <div className={css.icon}>
          <PointsIcon />
        </div>
        <div className={css.points}>צברת {animatedPoints} ניצוצות</div>
        {!auth.isLoggedIn && (
          <Link to={LOGIN_ROUTE} className={css.loginGoogleBtn}>
            <Button className={css.loginGoogleBtn}>
              <GoogleIcon />
              <p>התחברו כדי לצבור את הנקודות</p>
            </Button>
          </Link>
        )}
        <div className={css.footer}>
          <div className={css.label}>שתפו וזכו אחרים בתפילה</div>
          <div className={css.waIconWrap}>
            <WaIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

function formatPrayerDuration(totalSeconds: number): { label: string; unit: 'seconds' | 'minutes' } {
  if (totalSeconds < 60) {
    return { label: String(totalSeconds), unit: 'seconds' };
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return {
    label: `${minutes}:${String(seconds).padStart(2, '0')}`,
    unit: 'minutes',
  };
}

function useCountUp({
  to,
  durationMs,
  isActive,
  key,
}: {
  to: number;
  durationMs: number;
  isActive: boolean;
  key: number;
}) {
  const [value, setValue] = React.useState(0);
  const rafRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!isActive) return;

    const start = performance.now();
    const from = 0;
    const delta = to - from;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const next = Math.round(from + delta * t);
      setValue(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    setValue(0);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [durationMs, isActive, to, key]);

  return value;
}
